/**
 * @file services/models.ts
 * @description Service for handling models (fulfillments) data for Model Reviewer/Preparer Dashboard
 */

import { useQuery } from '@tanstack/react-query';
import { useSelector } from 'react-redux';
import { ApiService } from 'api/services/ApiService';
import { RootStateType } from 'dashboard409a/states/store';
import { UserRole } from './models.types';

// Query keys for models operations
export const ModelsQueryKeys = {
  MODELS_LIST: 'models-list',
} as const;

/**
 * Hook to get models list for all companies where user is reviewer/preparer
 */
export const useModelsList = (
  filterType: 'current' | 'past' = 'current',
  userRole: UserRole = UserRole.REVIEWER,
  page?: number
) => {
  // Get current user ID from Redux store
  const currentUserId = useSelector((state: RootStateType) => state.user.id);

  return useQuery({
    queryKey: [ModelsQueryKeys.MODELS_LIST, filterType, userRole, currentUserId],
    queryFn: async () => {
      if (!currentUserId) {
        throw new Error('User ID is required');
      }

      const response = await ApiService.apiDashboardUserModelsList(
        currentUserId.toString(),
        undefined,
        filterType
      );
      
      return response;
    },
    enabled: !!currentUserId,
    retry: false,
    refetchOnWindowFocus: false,
  });
};